import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import LoginFormView from './view';

const LoginForm = ({ onSuccess, onError, defaultEmail, setShowLogin, postAction, loginError: externalLoginError }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { doLogin, doGoogleLogin } = useAuth();
  
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState(externalLoginError || null);

  const handleSubmit = async (data) => {
    // Clear any previous errors
    setLoginError(null);
    
    try {
      // Show loading indicator
      setIsLoading(true);
      
      const response = await doLogin(data);
      
      // Reset loading state
      setIsLoading(false);
      
      if (onSuccess) {
        onSuccess(response);
        return;
      }
      
      if (postAction && postAction?.action === 'addBooking') {
        return;
      }
      
      // Only navigate if not handling via callback
      navigate('/');
      
    } catch (error) {
      // Reset loading state
      setIsLoading(false);
      
      // Set error state
      const errorMessage = t('login.error');
      setLoginError(errorMessage);
      
      // Call error callback if provided
      if (onError) {
        onError(data.email, data.password, errorMessage);
      }
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      setLoginError(null);
      setIsLoading(true);
      
      const googleData = { ...credentialResponse };
      
      if (postAction && postAction.group) {
        googleData.slug = postAction.group;
        googleData.action = "joinCommunity";
      }
      
      const response = await doGoogleLogin(googleData);
      
      setIsLoading(false);
      
      if (onSuccess) {
        onSuccess(response);
        return;
      }
      
      if (postAction && postAction?.action === 'addBooking') {
        return;
      }
      
      // Only navigate if not handling via callback
      navigate('/');
      
    } catch (error) {
      setIsLoading(false);
      
      const errorMessage = t('login.googleError');
      setLoginError(errorMessage);
      
      // No snackbar here, parent component will handle it
    }
  };

  // Update internal error state if external error prop changes
  React.useEffect(() => {
    if (externalLoginError && externalLoginError !== loginError) {
      setLoginError(externalLoginError);
    }
  }, [externalLoginError, loginError]);

  return (
    <LoginFormView
      onSubmit={handleSubmit}
      onGoogleLogin={handleGoogleLogin}
      defaultEmail={defaultEmail}
      setShowLogin={setShowLogin}
      isLoading={isLoading}
      loginError={loginError}
    />
  );
};

export default LoginForm;